import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import type {
  UpdateInvoiceDeliveryDatePayload,
} from 'contracts/tracking-v2/invoices';
import { useInvoiceStore } from 'pages/tracking-v2/invoice-details-page/hooks';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
} from 'components/ui/dialog';
import { DatePickerGs, TextareaGs } from 'components/ui/Forms';
import { Form, FormField } from 'components/ui/form';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components';

import { formSchema, type FormSchema } from './form-schema';

type Ref = {
  open: () => void;
  close: () => void;
}

export type UpdateInvoiceDeliveryDateDialogRef = Ref

type Props = {
  onSubmit: (payload: UpdateInvoiceDeliveryDatePayload) => void;
  loading?: boolean;
}

export const UpdateInvoiceDeliveryDateDialog = React.forwardRef<Ref, Props>(({
                                                                               onSubmit,
                                                                               loading,
                                                                             }, ref) => {
  const { invoice } = useInvoiceStore();
  const [open, setOpen] = React.useState(false);

  const minDate = React.useMemo(() => {
    if (!invoice?.infnfeIdeDhemi) return undefined;
    const dateOnly = format(new Date(invoice.infnfeIdeDhemi), 'yyyy-MM-dd');
    return new Date(dateOnly);
  }, [invoice?.infnfeIdeDhemi]);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const deliveryDateFieldValue = form.watch('deliveryDate');

  const datesAreSame = React.useMemo(() => {
    if (!invoice?.deliveryDate || !deliveryDateFieldValue) return false;
    const oldDeliveryDate = format(new Date(invoice.deliveryDate), 'yyyy-MM-dd');
    const newDeliveryDate = format(deliveryDateFieldValue, 'yyyy-MM-dd');
    return oldDeliveryDate === newDeliveryDate;
  }, [invoice?.deliveryDate, deliveryDateFieldValue]);

  const onFormSubmit = React.useCallback((formData: FormSchema) => {
    if (!invoice?.id) return;

    const { deliveryDate, description } = formData;

    if (deliveryDate < new Date(invoice.infnfeIdeDhemi)) {
      return form.setError('deliveryDate', {
        message: 'A data não pode ser anterior à data de emissão',
      });
    } else if (deliveryDate > new Date()) {
      return form.setError('deliveryDate', {
        message: 'A data não pode ser futura',
      });
    }

    onSubmit({
      invoiceId: invoice.id,
      deliveryDate: format(deliveryDate, 'yyyy-MM-dd'),
      description,
    });
  }, [onSubmit, invoice?.id]);

  const hydrateForm = React.useCallback(() => {
    if (invoice?.deliveryDate) {
      form.setValue('deliveryDate', new Date(invoice.deliveryDate));
    }
  }, [invoice?.deliveryDate]);

  const onOpenChange = React.useCallback((open: boolean) => {
    setOpen(open);
  }, []);

  React.useEffect(() => {
    hydrateForm();
  }, [hydrateForm]);

  React.useImperativeHandle(ref, () => ({
    open: () => onOpenChange(true),
    close: () => onOpenChange(false),
  }), [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'w-full max-w-[540px]'}>
        <DialogHeader className={'mb-6'}>
          <DialogTitle>Alterar data de entrega</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onFormSubmit)} className={'w-full'}>
            <div className={'flex flex-col gap-4'}>
              <FormField
                control={form.control}
                name={'deliveryDate'}
                render={({ field }) => (
                  <DatePickerGs
                    {...field}
                    label={'Data de entrega'}
                    fieldValue={field.value ? new Date(field.value) : undefined}
                    fieldOnChange={field.onChange}
                    minDate={minDate}
                    maxDate={new Date()}
                    portal={false}
                  />
                )}
              />
              <FormField
                control={form.control}
                name={'description'}
                render={({ field }) => (
                  <TextareaGs
                    {...field}
                    label={'Comentário'}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <div className={'grid grid-cols-2 gap-4 mt-6'}>
              <Button.Root
                type={'button'}
                size={'sm'}
                variant={'secondary'}
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button.Root>
              <Button.Root
                type={'submit'}
                size={'sm'}
                disabled={loading || datesAreSame}
              >
                {loading ? <ActivityIndicator /> : 'Salvar'}
              </Button.Root>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
UpdateInvoiceDeliveryDateDialog.displayName = 'UpdateInvoiceDeliveryDateDialog';